import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCustomerLicensing } from "../utils/api";
import { evaluateCspOpportunities } from "../utils/cspCostOptimization";
import {
  cspPromptPreferenceKey,
  readCspPromptPreference,
  writeCspPromptPreference,
} from "../utils/cspPromptPreference";

const EMPTY = Object.freeze({ eligibility: "unavailable", topics: [], participatingSkus: new Set(), snapshot: null });
const safeLocalStorage = () => {
  try { return window.localStorage; } catch { return null; }
};

export default function useCspOpportunities({ tenantId, objectId, slug }) {
  const identityKey = cspPromptPreferenceKey({ tenantId, objectId, slug });
  const generation = useRef(0);
  const [requestCycle, setRequestCycle] = useState(() => ({ identityKey, slug, id: 0 }));
  const [loaded, setLoaded] = useState({ cycle: null, result: EMPTY, error: false });
  const [preference, setPreference] = useState(() => readCspPromptPreference(safeLocalStorage(), identityKey));
  const requestChanged = requestCycle.identityKey !== identityKey || requestCycle.slug !== slug;
  const cycle = requestChanged ? requestCycle.id + 1 : requestCycle.id;

  // The request cycle is checked during render, before effects can run: an
  // earlier result cannot reappear during an A -> B -> A customer switch.
  const current = loaded.cycle === cycle ? loaded.result : EMPTY;
  const currentPreference = useMemo(
    () => readCspPromptPreference(safeLocalStorage(), identityKey),
    [identityKey]
  );
  const visiblePreference = preference.identityKey === identityKey ? preference : currentPreference;

  useEffect(() => {
    setPreference({ ...readCspPromptPreference(safeLocalStorage(), identityKey), identityKey });
    if (!identityKey) {
      setLoaded({ cycle, result: EMPTY, error: false });
      return undefined;
    }
    const request = ++generation.current;
    let alive = true;
    fetchCustomerLicensing(slug)
      .then((snapshot) => {
        if (!alive || request !== generation.current) return;
        setLoaded({ cycle, result: { ...evaluateCspOpportunities(snapshot), snapshot }, error: false });
      })
      .catch(() => {
        if (alive && request === generation.current) setLoaded({ cycle, result: EMPTY, error: true });
      });
    return () => { alive = false; };
  }, [cycle, identityKey, slug]);

  // React discards this render and immediately retries with the adjusted state,
  // so speculative renders cannot advance the committed request cycle.
  if (requestChanged) {
    setRequestCycle({ identityKey, slug, id: cycle });
  }

  const setHidden = (hidden) => {
    const next = { ...writeCspPromptPreference(safeLocalStorage(), identityKey, hidden), identityKey };
    setPreference(next);
  };

  return {
    ...current,
    hasIdentity: !!identityKey,
    loading: !!identityKey && loaded.cycle !== cycle,
    error: loaded.cycle === cycle && loaded.error === true,
    hidden: !!visiblePreference.hidden,
    preferencePersistence: visiblePreference.persistence,
    setHidden,
  };
}
